import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, MailCheck } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, checkAuth } = useAuth() || {};
  const [state, setState] = useState({ loading: !!token, ok: false, error: "" });
  const [resending, setResending] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!token) return; // Holding page — no token, just wait for user to check inbox.
    api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(() => { if (mounted) { setState({ loading: false, ok: true, error: "" }); checkAuth?.(); } })
      .catch((e) => { if (mounted) setState({ loading: false, ok: false, error: e?.response?.data?.detail || "Verification failed" }); });
    return () => { mounted = false; };
  }, [token, checkAuth]);

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent. Check your inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't resend");
    } finally { setResending(false); }
  };

  // ---- Holding page: user landed here without a token (post-signup gate) ----
  if (!token) {
    return (
      <div className="min-h-screen bg-kb-bg flex items-center justify-center p-6" data-testid="verify-email-holding">
        <div className="kb-card p-8 max-w-md w-full text-center">
          <MailCheck className="w-12 h-12 mx-auto text-kb-primary" />
          <h1 className="font-serif text-2xl text-kb-ink mt-4">Verify your email</h1>
          <p className="text-sm text-kb-ink-muted mt-2">
            We&apos;ve sent a verification link to <b>{user?.email || "your inbox"}</b>. Click it to unlock your KhojByte dashboard.
          </p>
          <button onClick={resend} disabled={resending} className="kb-btn-primary mt-6 inline-flex disabled:opacity-50" data-testid="resend-verify-btn">
            {resending ? "Sending…" : "Resend verification email"}
          </button>
          <Link to="/login" className="block mt-3 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted hover:text-kb-primary">Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kb-bg flex items-center justify-center p-6" data-testid="verify-email-page">
      <div className="kb-card p-8 max-w-md w-full text-center">
        {state.loading ? (
          <><Loader2 className="w-10 h-10 mx-auto text-kb-primary animate-spin" />
            <h1 className="font-serif text-2xl text-kb-ink mt-4">Verifying your email…</h1></>
        ) : state.ok ? (
          <><CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <h1 className="font-serif text-2xl text-kb-ink mt-4">Email verified</h1>
            <p className="text-sm text-kb-ink-muted mt-2">Thanks — your KhojByte account is fully secured.</p>
            <Link to={user ? "/app" : "/login"} className="kb-btn-primary mt-6 inline-flex" data-testid="verify-continue-btn">Continue</Link></>
        ) : (
          <><XCircle className="w-12 h-12 mx-auto text-rose-500" />
            <h1 className="font-serif text-2xl text-kb-ink mt-4">Verification failed</h1>
            <p className="text-sm text-kb-ink-muted mt-2">{state.error}. The link may have expired — request a new one from your dashboard.</p>
            <Link to="/login" className="kb-btn-outline mt-6 inline-flex">Back to sign in</Link></>
        )}
      </div>
    </div>
  );
}
